import DeliverableStatusModel from '@models/shop/DeliverableStatusModel';
import OrderItemUnitModel     from '@models/shop/OrderItemUnitModel';
import ShopServiceModel       from '@/models/ShopServiceModel';
import TimestampAble          from '@widesk-core/models/traits/TimestampAble';
import Blamable               from '@/models/traits/Blamable';
import ShippingMethodModel    from '@models/shop/ShippingMethodModel';
import ClientModel            from '@models/shop/ClientModel';
import _omitBy                from 'lodash/omitBy';
import DeliveryBatchModel     from '@models/shop/DeliveryBatchModel';
import DeliveryModel          from '@models/shop/DeliveryModel';
import { message }            from '@widesk-ui/hooks/useMessage';
import ApiCollection          from '@widesk-core/models/ApiCollection';
import WithEntertainmentPath  from '@/models/mixins/withEntetainmentPath';
import doc                    from '@widesk-core/decorators/doc';
import model                  from '@widesk-core/decorators/model';
import DeliverableGroupModel  from '@models/shop/DeliverableGroupModel';

const customDocModel = doc.model;

@model.urnResource('deliverable')
@doc.path('/deliverables/{?id}')
export default class DeliverableModel extends Blamable(TimestampAble(WithEntertainmentPath(ShopServiceModel))) {
	private declare _tsFilters: {
		'createdAt[after]': string | Date | Dayjs;
		'createdAt[before]': string | Date | Dayjs;
		'createdAt[strictly_after]': string | Date | Dayjs;
		'createdAt[strictly_before]': string | Date | Dayjs;
		'deliverableGroup': id;
		'deliverableShipmentAddresses.shipmentAddress': id;
		'deliverableStatus': id;
		'deliverableStatus.reference': Shop_DeliverableStatusReference;
		'field_search[owner_firstname]': string;
		'field_search[owner_lastname]': string;
		'orderItemUnit': id;
		'orderItemUnit.orderItem.order': id;
		'orderItemUnit.orderItem.order.client': id;
		'orderItemUnit.orderItem.order.client.clientType': id;
		'orderItemUnit.orderItem.order.client.userUrn': Urn;
		'orderItemUnit.orderItem.order.network': id;
		'orderItemUnit.orderItem.order.network.reference': Shop_NetworkReference;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.entertainment': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.entertainment.entertainmentUrn': Urn;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.productTaxonomies.taxonomy': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.productProductSets.productSet': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.productType': id;
		'orderItemUnit.orderItem.productPricingClientGroup.productPricing.productVariant.product.productType.reference': Shop_ProductTypeReference;
		'orderItemUnit.orderItem.productVariant': id;
		'orderItemUnit.orderItem.productVariant.product': id;
		'orderItemUnit.orderItem.productVariant.product.entertainment': id;
		'orderItemUnit.orderItem.productVariant.product.entertainment.entertainmentUrn': Urn;
		'orderItemUnit.orderItem.productVariant.product.productType': id;
		'orderItemUnit.orderItem.productVariant.product.productType.reference': Shop_ProductTypeReference;
		'owner': id;
		'owner.userUrn': Urn;
		'shippingMethod': id;
		'shippingMethod.reference': Shop_ShippingMethodReference;
		'tags[serviceUrn][tagReference]': id;
		'updatedAt[after]': string | Date | Dayjs;
		'updatedAt[before]': string | Date | Dayjs;
		'updatedAt[strictly_after]': string | Date | Dayjs;
		'updatedAt[strictly_before]': string | Date | Dayjs;
		'tags[jts:registry:service:shop][regenerated_from]': Urn;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'deliverableBadgeInfos.number': string;
		'id': string;
		'owner_firstname': string;
		'owner_lastname': string;
		'updatedAt': string;
	};

	@doc.model(DeliverableGroupModel) declare deliverableGroup: DeliverableGroupModel;
	@doc.model(DeliverableStatusModel) declare deliverableStatus: DeliverableStatusModel;
	@doc.string declare label: string;
	@doc.model(OrderItemUnitModel) declare orderItemUnit: OrderItemUnitModel;
	@doc.model(ShippingMethodModel) declare shippingMethod: ShippingMethodModel;

	@customDocModel(ClientModel) declare owner: ClientModel;

	public static async print(filters: ModelFilters<DeliverableModel>) {
		const shippingMethodCollection = new ApiCollection(ShippingMethodModel);
		await shippingMethodCollection.list();

		const shippingMethod = shippingMethodCollection.find(m => m.reference === 'print');

		const deliverableFilters = _omitBy(filters, (_, key) => {
			return ['step', 'page'].includes(key) || key.startsWith('order[');
		});

		const batch = new DeliveryBatchModel();
		await batch.patch({ label: 'Impression', shippingMethod: shippingMethod?.urn, deliverableFilters });

		const delivery = new DeliveryModel(batch.attributes);

		message.success(`La demande d'impression a bien été enregistrée`);

		return delivery;
	}

	public async regenerate(shippingMethodUrn?: Urn) {
		return this.save({ path: '/deliverables/_regenerate', method: 'post', patchAttributes: {
			deliverable: this.urn,
			shippingMethod: shippingMethodUrn || this.getUrn('shippingMethod' as any),
		}});
	}
}
